'use client'

import { useBetterMediaQuery } from '@/lib/hooks'
import { cn } from '@/lib/utils'
import { CheckIcon, CopyIcon } from 'lucide-react'
import {
  CSSProperties,
  ComponentProps,
  PropsWithChildren,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import ReactSyntaxHighlighter from 'react-syntax-highlighter'
import { atomOneDark } from 'react-syntax-highlighter/dist/esm/styles/hljs'
import { toast } from 'sonner'
import { Button } from '../ui/button'
import { isMobileQuery } from '../ui/responsive'
import { ScrollArea, ScrollBar } from '../ui/scroll-area'

type CodeBlockProps = PropsWithChildren<ComponentProps<'div'>> & {
  language?: string
}

const highlighterStyle = {
  ...atomOneDark,
  hljs: {
    background: 'var(--background)',
    padding: 'calc(var(--spacing) * 2)',
  },
}

export function CodeBlock({ className, language, children, ...props }: CodeBlockProps) {
  const [isCopied, setIsCopied] = useState(false)
  const [containerWidth, setContainerWidth] = useState(undefined)
  const isMobile = useBetterMediaQuery(isMobileQuery)
  const containerRef = useRef<HTMLDivElement>(null)

  const codeString = useMemo(() => {
    return typeof children === 'string'
      ? children
      : Array.isArray(children)
        ? children.join('')
        : String(children || '')
  }, [children])

  useEffect(() => {
    if (!containerRef.current) return

    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth)
      }
    }

    // Initial width calculation
    updateWidth()
  }, [])

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(codeString)
      setIsCopied(true)
      toast.success('Code copied to clipboard')

      // Reset the copied state after 2 seconds
      setTimeout(() => {
        setIsCopied(false)
      }, 2000)
    } catch (error) {
      toast.error('Failed to copy code')
      console.error('Failed to copy code:', error)
    }
  }

  return (
    <div
      {...props}
      className={cn(
        'bg-sidebar not-prose border-border relative w-full rounded-md border p-2 text-sm lg:text-base',
        className,
      )}
      ref={containerRef}
      style={
        containerWidth
          ? ({
              '--code-block-width': `calc(${containerWidth}px - calc(var(--spacing) * 4))`,
            } as CSSProperties)
          : undefined
      }
    >
      <div className="absolute top-2.5 right-3 z-20">
        <Button
          variant="secondary"
          size="icon"
          onClick={handleCopy}
          aria-label="Copy code to clipboard"
        >
          {isCopied ? (
            <CheckIcon className="h-4 w-4 text-green-500" />
          ) : (
            <CopyIcon className="h-4 w-4" />
          )}
        </Button>
      </div>
      <ScrollArea className="w-(--code-block-width) rounded-md whitespace-nowrap">
        {containerWidth && (
          <ReactSyntaxHighlighter
            style={highlighterStyle}
            showLineNumbers={!isMobile}
            language={language?.toLocaleLowerCase()}
          >
            {codeString}
          </ReactSyntaxHighlighter>
        )}
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
    </div>
  )
}
